import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RouteComponentProps } from 'react-router';
import { Route, Switch, useLocation, withRouter } from 'react-router';
import { useHistory } from 'react-router-dom';

import BackButton from '@swf/components/StarsModal/components/BackButton';
import CloseButton from '@swf/components/StarsModal/components/CloseButton';
import Header from '@swf/components/StarsModal/components/Header/Header';
import Translation from '@swf/components/Translation/Translation';
import { Country } from '@swf/core/enums';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { License } from '@swf/core/types/Account';
import { closeDocVerify } from '@swf/core/utils/postMessages';

import {
  TXTCLI_Change_Password,
  TXTCLI_Enter_PIN,
  TXTCLI_Login,
  TXTCLI_NemID_login,
  TXTCLI_PokerStars_PIN,
  TXTCLI_RSA_Passcode,
  TXTCLI_SMS_Validation,
  TXTWCL_Fun_Play_XXAccountBrandXX_Acco_ELL,
  TXTWCL_Online_Software_Terms_of_Servi_ELL,
  TXTWCL_Session_Transfer_Request,
  TXTWCL_Stars_Account_Created,
  TXTWCL_Verify_your_identity
} from './AuthHeader.messages.json';
import RegistrationHeader from './components/RegistrationHeader';

const AuthHeader: React.FC = () => {
  const KYCRequired = useSelector(select.account.isKYCRequired);
  const realMoneyOK = useSelector(select.account.isRealMoneyOK);
  const ageVerified = useSelector(select.account.isAgeVerified);
  const authVerified = useSelector(select.account.isAuthVerified);
  const country = useSelector(select.account.getCountry);
  const license = useSelector(select.account.getLicense);
  const { state: locationState } = useLocation();
  const { auth } = useDispatch<Dispatch>();

  const { push } = useHistory();

  const back = () => push('/auth/registration', locationState);

  const handleClose = async ({ match: { isExact } }: RouteComponentProps) => {
    if (!isExact) {
      await auth.signEula({ marketingMailList: false });
    }
    auth.logout({});
  };

  const handleDocVerifynClose = () => {
    if (KYCRequired && !realMoneyOK && !ageVerified && !authVerified && license === License.UK)
      auth.logout({});
    closeDocVerify();
  };

  return (
    <Header>
      <Route exact path="/auth/registration/eula" render={() => <BackButton onClick={back} />} />
      <Switch>
        <Route path="/auth/login/nemid" render={() => <Translation {...TXTCLI_NemID_login} />} />
        <Route path="/auth/login/pin" render={() => <Translation {...TXTCLI_Enter_PIN} />} />
        <Route path="/auth/login/rsa" render={() => <Translation {...TXTCLI_RSA_Passcode} />} />
        <Route
          path="/auth/login/eula"
          render={() => <Translation {...TXTWCL_Online_Software_Terms_of_Servi_ELL} />}
        />
        <Route
          path="/auth/login/pin-reset"
          render={() => <Translation {...TXTCLI_PokerStars_PIN} />}
        />
        <Route
          path="/auth/registration/welcome"
          render={() => (
            <Translation
              {...(country === Country.Netherlands && license === License.EU
                ? TXTWCL_Fun_Play_XXAccountBrandXX_Acco_ELL
                : TXTWCL_Stars_Account_Created)}
            />
          )}
        />
        <Route path="/auth/registration/bankid" component={RegistrationHeader} />
        <Route path="/auth/registration" component={RegistrationHeader} />
        <Route
          path="/auth/docverify"
          render={() => <Translation {...TXTWCL_Verify_your_identity} />}
        />
        <Route
          path="/auth/login/sms/:resent?"
          render={() => <Translation {...TXTCLI_SMS_Validation} />}
        />
        <Route
          path="/auth/force-change-password"
          render={() => <Translation {...TXTCLI_Change_Password} />}
        />
        <Route
          path="/auth/session-transfer"
          render={() => <Translation {...TXTWCL_Session_Transfer_Request} />}
        />
        <Route render={() => <Translation {...TXTCLI_Login} />} />
      </Switch>
      <Switch>
        <Route
          path="/auth/login/sms/:resent?"
          render={() => <CloseButton onClick={() => auth.logout({})} />}
        />
        <Route path="/auth/force-change-password" render={() => false} />
        <Route
          path="/auth/login/eula"
          render={(props) => <CloseButton onClick={() => handleClose(props)} />}
        />
        <Route
          path="/auth/docverify"
          render={() => <CloseButton onClick={handleDocVerifynClose} />}
        />
        <Route render={() => <CloseButton />} />
      </Switch>
    </Header>
  );
};

export default withRouter(AuthHeader);
